import { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, Save, X, MapPin, ChevronDown, Star } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { captureDeviceLocation } from '../lib/geolocation';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES, computeChanges } from '../lib/auditLog';
import { WaterScheduleManager, WaterSchedule } from './WaterScheduleManager';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';

interface Manufacturer {
  id: string;
  name: string;
  created_at: string;
}

interface ZoneTypeRow {
  id: string;
  name: string;
  is_default: boolean | null;
  created_at: string;
}

interface ZoneType {
  id: string;
  name: string;
  is_default: boolean;
  created_at: string;
}

export function ControllerSettings() {
  const { notify, NotifyModal } = useNotify();
  const [deleteManufacturerConfirm, setDeleteManufacturerConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleteZoneTypeConfirm, setDeleteZoneTypeConfirm] = useState<{ id: string; name: string } | null>(null);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [zoneTypes, setZoneTypes] = useState<ZoneType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingManufacturerId, setEditingManufacturerId] = useState<string | null>(null);
  const [isAddingManufacturer, setIsAddingManufacturer] = useState(false);
  const [manufacturerFormData, setManufacturerFormData] = useState({ name: '' });
  const [editingZoneTypeId, setEditingZoneTypeId] = useState<string | null>(null);
  const [isAddingZoneType, setIsAddingZoneType] = useState(false);
  const [zoneTypeFormData, setZoneTypeFormData] = useState({ name: '', is_default: false });

  const [waterSchedule, setWaterSchedule] = useState<WaterSchedule>({ programs: [] });
  const [gpsLocation, setGpsLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const [isManufacturersExpanded, setIsManufacturersExpanded] = useState(false);

  useEffect(() => {
    void Promise.all([fetchManufacturers(), fetchZoneTypes()]);
  }, []);

  const fetchManufacturers = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<Manufacturer[]>('/api/controller-manufacturers');
      const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
      setManufacturers(sorted);
    } catch (err) {
      console.error('Error fetching manufacturers:', err);
      notify('Error', 'Failed to load manufacturers', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const fetchZoneTypes = async () => {
    try {
      const data = await apiClient.get<ZoneTypeRow[]>('/api/zone-types');
      const normalized = data.map((zt) => ({ ...zt, is_default: zt.is_default ?? false }));
      const sorted = normalized.sort((a, b) => {
        if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      setZoneTypes(sorted);
    } catch (err) {
      console.error('Error fetching zone types:', err);
      notify('Error', 'Failed to load zone types', 'danger');
    }
  };

  const handleAddManufacturer = () => {
    setIsAddingManufacturer(true);
    setManufacturerFormData({ name: '' });
  };

  const handleEditManufacturer = (manufacturer: Manufacturer) => {
    setEditingManufacturerId(manufacturer.id);
    setManufacturerFormData({ name: manufacturer.name });
  };

  const handleCancelManufacturer = () => {
    setIsAddingManufacturer(false);
    setEditingManufacturerId(null);
    setManufacturerFormData({ name: '' });
  };

  const handleSaveManufacturer = async () => {
    if (!manufacturerFormData.name.trim()) {
      notify('Notice', 'Please enter a manufacturer name', 'warning');
      return;
    }

    try {
      if (isAddingManufacturer) {
        const created = await apiClient.post<Manufacturer>('/api/controller-manufacturers', manufacturerFormData);
        await logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.MANUFACTURER,
          entityId: created.id,
          entityLabel: manufacturerFormData.name,
          details: { name: manufacturerFormData.name },
          pageContext: 'controller-settings',
        });
      } else if (editingManufacturerId) {
        await apiClient.put(`/api/controller-manufacturers/${editingManufacturerId}`, manufacturerFormData);
        const orig = manufacturers.find((m) => m.id === editingManufacturerId);
        await logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.MANUFACTURER,
          entityId: editingManufacturerId,
          entityLabel: manufacturerFormData.name,
          details: computeChanges({ name: orig?.name }, { name: manufacturerFormData.name }),
          pageContext: 'controller-settings',
        });
      }

      await fetchManufacturers();
      handleCancelManufacturer();
    } catch (err) {
      console.error('Error saving manufacturer:', err);
      notify('Error', 'Failed to save manufacturer', 'danger');
    }
  };

  const handleDeleteManufacturer = (id: string, name: string) => {
    setDeleteManufacturerConfirm({ id, name });
  };

  const confirmDeleteManufacturer = async () => {
    if (!deleteManufacturerConfirm) return;
    const { id, name } = deleteManufacturerConfirm;
    try {
      await apiClient.del(`/api/controller-manufacturers/${id}`);
      await logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.MANUFACTURER,
        entityId: id,
        entityLabel: name,
        pageContext: 'controller-settings',
      });
      setDeleteManufacturerConfirm(null);
      await fetchManufacturers();
    } catch (err) {
      console.error('Error deleting manufacturer:', err);
      setDeleteManufacturerConfirm(null);
      notify('Error', 'Failed to delete manufacturer', 'danger');
    }
  };

  const handleAddZoneType = () => {
    setIsAddingZoneType(true);
    setZoneTypeFormData({ name: '', is_default: false });
  };

  const handleEditZoneType = (zoneType: ZoneType) => {
    setEditingZoneTypeId(zoneType.id);
    setZoneTypeFormData({ name: zoneType.name, is_default: zoneType.is_default });
  };

  const handleCancelZoneType = () => {
    setIsAddingZoneType(false);
    setEditingZoneTypeId(null);
    setZoneTypeFormData({ name: '', is_default: false });
  };

  const handleSaveZoneType = async () => {
    if (!zoneTypeFormData.name.trim()) {
      notify('Notice', 'Please enter a zone type name', 'warning');
      return;
    }

    try {
      if (isAddingZoneType) {
        const created = await apiClient.post<ZoneType>('/api/zone-types', zoneTypeFormData);
        await logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.ZONE_TYPE,
          entityId: created.id,
          entityLabel: zoneTypeFormData.name,
          details: { name: zoneTypeFormData.name, is_default: zoneTypeFormData.is_default },
          pageContext: 'controller-settings',
        });
      } else if (editingZoneTypeId) {
        await apiClient.put(`/api/zone-types/${editingZoneTypeId}`, zoneTypeFormData);
        const orig = zoneTypes.find((z) => z.id === editingZoneTypeId);
        await logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.ZONE_TYPE,
          entityId: editingZoneTypeId,
          entityLabel: zoneTypeFormData.name,
          details: computeChanges(
            { name: orig?.name, is_default: orig?.is_default },
            { name: zoneTypeFormData.name, is_default: zoneTypeFormData.is_default },
          ),
          pageContext: 'controller-settings',
        });
      }

      await fetchZoneTypes();
      handleCancelZoneType();
    } catch (err) {
      console.error('Error saving zone type:', err);
      notify('Error', 'Failed to save zone type', 'danger');
    }
  };

  const handleDeleteZoneType = (id: string, name: string) => {
    setDeleteZoneTypeConfirm({ id, name });
  };

  const confirmDeleteZoneType = async () => {
    if (!deleteZoneTypeConfirm) return;
    const { id, name } = deleteZoneTypeConfirm;
    try {
      await apiClient.del(`/api/zone-types/${id}`);
      await logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.ZONE_TYPE,
        entityId: id,
        entityLabel: name,
        pageContext: 'controller-settings',
      });
      setDeleteZoneTypeConfirm(null);
      await fetchZoneTypes();
    } catch (err) {
      console.error('Error deleting zone type:', err);
      setDeleteZoneTypeConfirm(null);
      notify('Error', 'Failed to delete zone type', 'danger');
    }
  };

  const handleSetDefaultZoneType = async (id: string) => {
    try {
      const zt = zoneTypes.find((z) => z.id === id);
      if (!zt) return;
      // The DB has a BEFORE INSERT/UPDATE trigger on zone_types that clears
      // other rows' is_default when one is set, so a single PUT is atomic.
      await apiClient.put(`/api/zone-types/${id}`, { name: zt.name, is_default: true });
      await logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.ZONE_TYPE,
        entityId: id,
        entityLabel: zt.name,
        details: { is_default: true },
        pageContext: 'controller-settings',
      });
      await fetchZoneTypes();
    } catch (err) {
      console.error('Error setting default zone type:', err);
      notify('Error', 'Failed to set default zone type', 'danger');
    }
  };

  const getCurrentLocation = () => {
    setIsGettingLocation(true);
    if (!navigator.geolocation) {
      notify('Notice', 'Geolocation is not supported by your browser', 'warning');
      setIsGettingLocation(false);
      return;
    }

    captureDeviceLocation(
      'controller_settings',
      (position) => {
        setGpsLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setIsGettingLocation(false);
      },
      (err) => {
        console.error('Error getting location:', err);
        notify('Error', 'Failed to get location. Please ensure location permissions are enabled.', 'danger');
        setIsGettingLocation(false);
      },
    );
  };

  const clearLocation = () => {
    setGpsLocation(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-theme-secondary">Loading controller settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="bg-surface rounded-xl shadow-sm border border-line">
        <div className="px-6 py-4 border-b border-line">
          <h2 className="text-2xl font-bold text-theme-primary">Controller Default Settings</h2>
          <p className="text-sm text-theme-secondary mt-1">Configure default settings for new controllers</p>
        </div>

        <div className="p-6 space-y-6">
          <WaterScheduleManager schedule={waterSchedule} onChange={setWaterSchedule} />

          <div>
            <label className="block text-sm font-medium text-theme-primary mb-3">
              GPS Location
            </label>
            {gpsLocation ? (
              <div className="p-4 bg-subtle rounded-lg border border-line">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <MapPin className="text-blue-600 mt-0.5" size={20} />
                    <div>
                      <p className="text-sm font-medium text-theme-primary">Location Set</p>
                      <p className="text-xs text-theme-primary mt-1">
                        Latitude: {gpsLocation.latitude.toFixed(6)}
                      </p>
                      <p className="text-xs text-theme-primary">
                        Longitude: {gpsLocation.longitude.toFixed(6)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={clearLocation}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Clear location"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={getCurrentLocation}
                disabled={isGettingLocation}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MapPin size={18} />
                {isGettingLocation ? 'Getting Location...' : 'Get Current Location'}
              </button>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t border-line">
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Default Settings
            </button>
          </div>
        </div>
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line">
        <div className="px-6 py-4 border-b border-line">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-theme-primary">Controller Properties</h2>
              <p className="text-sm text-theme-secondary mt-1">Manage additional properties collected for controllers</p>
            </div>
            <button
              onClick={() => setIsManufacturersExpanded(!isManufacturersExpanded)}
              className="p-2 hover:bg-subtle rounded-lg transition-colors"
            >
              <ChevronDown
                size={24}
                className={`transform transition-transform ${isManufacturersExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        </div>

        {isManufacturersExpanded && (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-theme-primary mb-4">Manufacturers</h3>
            <div className="mb-4 flex justify-end">
              {!isAddingManufacturer && (
                <button
                  onClick={handleAddManufacturer}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus size={18} />
                  Add Manufacturer
                </button>
              )}
            </div>

            {isAddingManufacturer && (
              <div className="mb-4 p-4 bg-subtle rounded-lg border border-line">
                <h3 className="text-lg font-semibold text-theme-primary mb-3">Add New Manufacturer</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-theme-primary mb-1">
                      Manufacturer Name *
                    </label>
                    <input
                      type="text"
                      value={manufacturerFormData.name}
                      onChange={(e) => setManufacturerFormData({ name: e.target.value })}
                      placeholder="e.g., Hunter Industries"
                      className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveManufacturer}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Save size={18} />
                      Save
                    </button>
                    <button
                      onClick={handleCancelManufacturer}
                      className="flex items-center gap-2 px-4 py-2 bg-subtle text-theme-primary rounded-lg hover:bg-subtle transition-colors"
                    >
                      <X size={18} />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-theme-primary">Manufacturer Name</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-theme-primary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {manufacturers.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-center py-8 text-theme-secondary">
                        No manufacturers found. Add your first manufacturer to get started.
                      </td>
                    </tr>
                  ) : (
                    manufacturers.map((manufacturer) => (
                      <tr key={manufacturer.id} className="border-b border-line hover:bg-subtle">
                        {editingManufacturerId === manufacturer.id ? (
                          <>
                            <td className="py-3 px-4">
                              <input
                                type="text"
                                value={manufacturerFormData.name}
                                onChange={(e) => setManufacturerFormData({ name: e.target.value })}
                                className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              />
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={handleSaveManufacturer}
                                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                                  title="Save"
                                >
                                  <Save size={18} />
                                </button>
                                <button
                                  onClick={handleCancelManufacturer}
                                  className="p-2 text-theme-secondary hover:bg-subtle rounded-lg transition-colors"
                                  title="Cancel"
                                >
                                  <X size={18} />
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-3 px-4 text-sm font-medium text-theme-primary">
                              {manufacturer.name}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEditManufacturer(manufacturer)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <Edit size={18} />
                                </button>
                                <button
                                  onClick={() => handleDeleteManufacturer(manufacturer.id, manufacturer.name)}
                                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-t border-line pt-6 mt-8">
              <h3 className="text-lg font-semibold text-theme-primary mb-4">Zone Types</h3>
              <div className="mb-4 flex justify-end">
                {!isAddingZoneType && (
                  <button
                    onClick={handleAddZoneType}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={18} />
                    Add Zone Type
                  </button>
                )}
              </div>

              {isAddingZoneType && (
                <div className="mb-4 p-4 bg-subtle rounded-lg border border-line">
                  <h3 className="text-lg font-semibold text-theme-primary mb-3">Add New Zone Type</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-theme-primary mb-1">
                        Zone Type Name *
                      </label>
                      <input
                        type="text"
                        value={zoneTypeFormData.name}
                        onChange={(e) => setZoneTypeFormData({ ...zoneTypeFormData, name: e.target.value })}
                        placeholder="e.g., Rotor"
                        className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_default_new_zone"
                        checked={zoneTypeFormData.is_default}
                        onChange={(e) => setZoneTypeFormData({ ...zoneTypeFormData, is_default: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="is_default_new_zone" className="text-sm text-theme-primary">
                        Set as default zone type
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveZoneType}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Save size={18} />
                        Save
                      </button>
                      <button
                        onClick={handleCancelZoneType}
                        className="flex items-center gap-2 px-4 py-2 bg-subtle text-theme-primary rounded-lg hover:bg-subtle transition-colors"
                      >
                        <X size={18} />
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto mb-8">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-theme-primary">Zone Type Name</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-theme-primary">Default</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-theme-primary">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zoneTypes.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center py-8 text-theme-secondary">
                          No zone types found. Add your first zone type to get started.
                        </td>
                      </tr>
                    ) : (
                      zoneTypes.map((zoneType) => (
                        <tr key={zoneType.id} className="border-b border-line hover:bg-subtle">
                          {editingZoneTypeId === zoneType.id ? (
                            <>
                              <td className="py-3 px-4">
                                <input
                                  type="text"
                                  value={zoneTypeFormData.name}
                                  onChange={(e) => setZoneTypeFormData({ ...zoneTypeFormData, name: e.target.value })}
                                  className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                              </td>
                              <td className="py-3 px-4 text-center">
                                <input
                                  type="checkbox"
                                  checked={zoneTypeFormData.is_default}
                                  onChange={(e) => setZoneTypeFormData({ ...zoneTypeFormData, is_default: e.target.checked })}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={handleSaveZoneType}
                                    className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                                    title="Save"
                                  >
                                    <Save size={18} />
                                  </button>
                                  <button
                                    onClick={handleCancelZoneType}
                                    className="p-2 text-theme-secondary hover:bg-subtle rounded-lg transition-colors"
                                    title="Cancel"
                                  >
                                    <X size={18} />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-3 px-4 text-sm font-medium text-theme-primary">
                                {zoneType.name}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleSetDefaultZoneType(zoneType.id)}
                                  className={`p-1 rounded transition-colors ${
                                    zoneType.is_default
                                      ? 'text-yellow-500 cursor-default'
                                      : 'text-theme-secondary hover:text-yellow-500 cursor-pointer'
                                  }`}
                                  title={zoneType.is_default ? 'Default zone type' : 'Set as default'}
                                  disabled={zoneType.is_default}
                                >
                                  <Star size={18} fill={zoneType.is_default ? 'currentColor' : 'none'} />
                                </button>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => handleEditZoneType(zoneType)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                                    title="Edit"
                                  >
                                    <Edit size={18} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteZoneType(zoneType.id, zoneType.name)}
                                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                    title={zoneType.is_default ? 'Cannot delete default zone type' : 'Delete'}
                                    disabled={zoneType.is_default}
                                  >
                                    <Trash2 size={18} />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-t border-line pt-6 mt-8">
              <h3 className="text-lg font-semibold text-theme-primary mb-4">Additional Controller Fields</h3>
              <div className="space-y-3 bg-subtle rounded-lg p-4">
                <div className="flex items-center justify-between py-2 px-3 bg-surface rounded border border-line">
                  <div>
                    <p className="font-medium text-theme-primary">Model</p>
                    <p className="text-sm text-theme-secondary">Controller model number or name</p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300 rounded-full">Active</span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-surface rounded border border-line">
                  <div>
                    <p className="font-medium text-theme-primary">Serial Number</p>
                    <p className="text-sm text-theme-secondary">Controller serial number</p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300 rounded-full">Active</span>
                </div>
              </div>
              <p className="text-sm text-theme-secondary mt-4">
                These fields will be available when adding or editing controllers at properties.
              </p>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={deleteManufacturerConfirm !== null}
        title="Delete Manufacturer"
        message={deleteManufacturerConfirm ? `Are you sure you want to delete the manufacturer "${deleteManufacturerConfirm.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDeleteManufacturer}
        onCancel={() => setDeleteManufacturerConfirm(null)}
      />

      <ConfirmModal
        isOpen={deleteZoneTypeConfirm !== null}
        title="Delete Zone Type"
        message={deleteZoneTypeConfirm ? `Are you sure you want to delete the zone type "${deleteZoneTypeConfirm.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDeleteZoneType}
        onCancel={() => setDeleteZoneTypeConfirm(null)}
      />

      {NotifyModal}
    </div>
  );
}
